/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Click-to-dial where the record has more than one number to choose from.
 *
 * An account has many contacts and a case has a contact plus its account's
 * contacts, so "the phone number" is not a single value the way it is on a
 * lead. One number dials straight through -- the menu would be a pointless
 * extra click -- and several open a picker.
 *
 * Renders nothing when there is nothing to dial, matching CallButton, so this
 * can be dropped onto a record header unconditionally.
 *
 * Usage:
 *   <CallContactMenu
 *     targets={[{ id: 1, name: "Ana Diaz", phone: "+61...", contactId: 1 }]}
 *     parentTable="accounts" parentId={account.id} accountId={account.id} />
 */

import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { IconButton, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { Call } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  commonIconButtonStyles,
  commonIconStyles,
} from "assets/theme/components/iconButtonStyles";
import { useTelephony } from "telephony/TelephonyContext";
import CallButton from "./CallButton";

export default function CallContactMenu({
  targets,
  parentTable,
  parentId,
  accountId,
  caseId,
  tone,
}) {
  const { enabled } = useTelephony();
  const [anchor, setAnchor] = useState(null);

  // Two records can carry the same number (a case's contact is usually also
  // one of the account's contacts); offering it twice is just confusing.
  const dialable = useMemo(() => {
    const seen = new Set();
    return (targets || []).filter((t) => {
      const phone = (t?.phone || "").trim();
      if (!phone || seen.has(phone)) return false;
      seen.add(phone);
      return true;
    });
  }, [targets]);

  if (!enabled || dialable.length === 0) return null;

  const commonProps = (target) => ({
    phone: target.phone,
    parentTable,
    parentId,
    accountId,
    caseId,
    contactId: target.contactId,
  });

  if (dialable.length === 1) {
    return <CallButton {...commonProps(dialable[0])} variant="icon" tone={tone} />;
  }

  const isHeader = tone === "header";

  return (
    <>
      <Tooltip title="Call a contact" placement="top">
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          // No size in header tone -- see CallButton: "small" forces an 18px
          // glyph that does not match the 24px siblings.
          size={isHeader ? undefined : "small"}
          color={isHeader ? undefined : "info"}
          sx={isHeader ? commonIconButtonStyles : undefined}
        >
          <Call {...(isHeader ? { sx: commonIconStyles } : { fontSize: "small" })} />
        </IconButton>
      </Tooltip>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {dialable.map((target) => (
          <MenuItem
            key={target.id ?? target.phone}
            disableRipple
            sx={{ display: "flex", gap: 2, justifyContent: "space-between" }}
          >
            <ListItemText
              primary={
                <MDTypography variant="button" fontWeight="medium">
                  {target.name || target.phone}
                </MDTypography>
              }
              secondary={
                <MDTypography variant="caption" color="text">
                  {target.label ? `${target.label} · ${target.phone}` : target.phone}
                </MDTypography>
              }
            />
            <MDBox onClick={() => setAnchor(null)}>
              <CallButton {...commonProps(target)} variant="button" label="Call" />
            </MDBox>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

CallContactMenu.propTypes = {
  targets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      phone: PropTypes.string,
      // Free-text qualifier shown beside the number, e.g. "Primary contact".
      label: PropTypes.string,
      contactId: PropTypes.number,
    })
  ),
  parentTable: PropTypes.string,
  parentId: PropTypes.number,
  accountId: PropTypes.number,
  caseId: PropTypes.number,
  tone: PropTypes.oneOf(["default", "header"]),
};

CallContactMenu.defaultProps = {
  targets: [],
  parentTable: null,
  parentId: null,
  accountId: null,
  caseId: null,
  tone: "default",
};
